import { useCallback, useEffect, useMemo, useState } from 'react';
import { FlaskConical, Loader2 } from 'lucide-react';
import { hmrcAPI } from '../../../api';
import { Button } from '../../ui/Button';
import { Input } from '../../ui/Input';
import HmrcSandboxCredentialsPanel from './HmrcSandboxCredentialsPanel';
import {
  EORI_RELATED_SERVICE_KEYS,
  NINO_RELATED_SERVICE_KEYS,
  SERVICE_GROUP_LABELS,
} from './constants';

const GROUP_ORDER = ['vat', 'incomeTax', 'customs', 'paye', 'agent', 'other'];

function filterGroupsForUserType(groups, userType) {
  if (!groups) return {};
  const result = {};
  for (const [groupId, items] of Object.entries(groups)) {
    const filtered = (items || []).filter((s) => s.allowedUserTypes?.includes(userType));
    if (filtered.length > 0) result[groupId] = filtered;
  }
  return result;
}

function serviceAllowedForUserType(serviceKey, services, userType) {
  const svc = services?.find((s) => s.key === serviceKey);
  return svc?.allowedUserTypes?.includes(userType) ?? false;
}

export default function HmrcSandboxTestUser() {
  const [options, setOptions] = useState(null);
  const [loadingOptions, setLoadingOptions] = useState(true);
  const [optionsError, setOptionsError] = useState(null);

  const [userType, setUserType] = useState('individual');
  const [selectedServices, setSelectedServices] = useState([]);
  const [nino, setNino] = useState('');
  const [eoriNumber, setEoriNumber] = useState('');
  const [defaultsApplied, setDefaultsApplied] = useState(false);

  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState(null);
  const [credentials, setCredentials] = useState(null);
  const [lastEnrolledServices, setLastEnrolledServices] = useState([]);

  const loadOptions = useCallback(async () => {
    setLoadingOptions(true);
    setOptionsError(null);
    try {
      const data = await hmrcAPI.getTestUserOptions();
      setOptions(data);
    } catch (err) {
      setOptionsError(err.response?.data?.error || err.message || 'Failed to load sandbox options');
    } finally {
      setLoadingOptions(false);
    }
  }, []);

  useEffect(() => {
    loadOptions();
  }, [loadOptions]);

  useEffect(() => {
    if (!options?.services || defaultsApplied) return;
    if (serviceAllowedForUserType('mtd-vat', options.services, 'individual')) {
      setSelectedServices(['mtd-vat']);
    }
    setDefaultsApplied(true);
  }, [options, defaultsApplied]);

  const filteredGroups = useMemo(
    () => filterGroupsForUserType(options?.groups, userType),
    [options?.groups, userType]
  );

  const showNino = selectedServices.some((key) => NINO_RELATED_SERVICE_KEYS.has(key));
  const showEori = selectedServices.some((key) => EORI_RELATED_SERVICE_KEYS.has(key));

  const availablePresets = useMemo(() => {
    if (!options?.presets) return [];
    return Object.entries(options.presets).filter(([, preset]) => preset.userTypes?.includes(userType));
  }, [options?.presets, userType]);

  const handleUserTypeChange = (nextType) => {
    setUserType(nextType);
    setSelectedServices((prev) =>
      prev.filter((key) => serviceAllowedForUserType(key, options?.services, nextType))
    );
    setCreateError(null);
  };

  const toggleService = (serviceKey) => {
    setSelectedServices((prev) =>
      prev.includes(serviceKey) ? prev.filter((k) => k !== serviceKey) : [...prev, serviceKey]
    );
    setCreateError(null);
  };

  const applyPreset = (presetKey) => {
    const preset = options?.presets?.[presetKey];
    if (!preset?.userTypes?.includes(userType)) return;
    setSelectedServices([...preset.serviceNames]);
    setCreateError(null);
  };

  const handleCreate = async () => {
    setCreating(true);
    setCreateError(null);
    try {
      const payload = {
        userType,
        serviceNames: selectedServices,
      };
      if (showNino && nino.trim()) payload.nino = nino.trim();
      if (showEori && eoriNumber.trim()) payload.eoriNumber = eoriNumber.trim();

      const result = await hmrcAPI.createTestUser(payload);
      setCredentials(result);
      setLastEnrolledServices([...selectedServices]);
    } catch (err) {
      setCreateError(err.response?.data?.error || err.message || 'Failed to create sandbox user');
    } finally {
      setCreating(false);
    }
  };

  if (loadingOptions) {
    return (
      <div className="flex items-center gap-2 py-6 text-[var(--ds-text-muted)]">
        <Loader2 className="h-5 w-5 animate-spin" />
        <span className="text-micro">Loading HMRC sandbox services…</span>
      </div>
    );
  }

  if (optionsError) {
    return (
      <div className="rounded-md border border-[var(--ds-status-danger)]/30 bg-[var(--ds-status-danger)]/10 px-3 py-2 text-micro text-[var(--ds-status-danger)]">
        {optionsError}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="space-y-4">
        <div>
          <span className="block text-meta font-medium text-gray-700 dark:text-gray-300 mb-2">User type</span>
          <div className="flex flex-wrap gap-4">
            {['individual', 'organisation'].map((type) => (
              <label key={type} className="inline-flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="hmrcTestUserType"
                  value={type}
                  checked={userType === type}
                  onChange={() => handleUserTypeChange(type)}
                  className="h-4 w-4 border-gray-300 dark:border-gray-600 text-[var(--ds-accent-primary)] focus:ring-[var(--ds-accent-primary)]"
                />
                <span className="text-micro text-[var(--ds-text-primary)] capitalize">{type}</span>
              </label>
            ))}
          </div>
        </div>

        {availablePresets.length > 0 && (
          <div>
            <span className="block text-meta font-medium text-gray-700 dark:text-gray-300 mb-2">
              Quick presets
            </span>
            <div className="flex flex-wrap gap-2">
              {availablePresets.map(([presetKey, preset]) => (
                <Button
                  key={presetKey}
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => applyPreset(presetKey)}
                >
                  {preset.label}
                </Button>
              ))}
            </div>
          </div>
        )}

        <div>
          <span className="block text-meta font-medium text-gray-700 dark:text-gray-300 mb-2">
            HMRC service enrolments
          </span>
          <p className="text-micro text-[var(--ds-text-muted)] mb-3">
            Select services to enrol on the test user. Include <code className="bg-[var(--ds-control-field-bg)] px-1 rounded text-xs">mtd-vat</code> to generate a VRN.
          </p>
          <div className="space-y-4 max-h-72 overflow-y-auto rounded-[var(--ds-radius-md)] border border-[var(--ds-border-subtle)] p-3 bg-[var(--ds-surface-panel2)]">
            {GROUP_ORDER.filter((gid) => filteredGroups[gid]?.length).map((groupId) => (
              <div key={groupId}>
                <h5 className="text-micro font-semibold text-[var(--ds-text-primary)] mb-2">
                  {SERVICE_GROUP_LABELS[groupId] || groupId}
                </h5>
                <ul className="space-y-2">
                  {filteredGroups[groupId].map((service) => (
                    <li key={service.key}>
                      <label className="inline-flex items-start gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={selectedServices.includes(service.key)}
                          onChange={() => toggleService(service.key)}
                          className="mt-0.5 h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-[var(--ds-accent-primary)] focus:ring-[var(--ds-accent-primary)]"
                        />
                        <span className="text-micro text-[var(--ds-text-primary)]">
                          <span className="font-medium">{service.name}</span>
                          <span className="block text-[var(--ds-text-muted)] font-mono text-xs">{service.key}</span>
                        </span>
                      </label>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>

        {showNino && (
          <div>
            <label className="block text-meta font-medium text-gray-700 dark:text-gray-300 mb-1">
              NINO (optional)
            </label>
            <Input
              type="text"
              value={nino}
              onChange={(e) => setNino(e.target.value)}
              placeholder="AB123456C"
              className="h-8 border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
            />
            <p className="text-micro text-[var(--ds-text-muted)] mt-1">
              Leave blank for HMRC to generate. Required format when provided: AB123456C.
            </p>
          </div>
        )}

        {showEori && (
          <div>
            <label className="block text-meta font-medium text-gray-700 dark:text-gray-300 mb-1">
              EORI number (optional)
            </label>
            <Input
              type="text"
              value={eoriNumber}
              onChange={(e) => setEoriNumber(e.target.value)}
              placeholder="GB123456789000"
              className="h-8 border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
            />
          </div>
        )}

        {createError && (
          <div className="rounded-md border border-[var(--ds-status-danger)]/30 bg-[var(--ds-status-danger)]/10 px-3 py-2 text-micro text-[var(--ds-status-danger)]">
            {createError}
          </div>
        )}

        <Button
          type="button"
          onClick={handleCreate}
          disabled={creating}
          className="gap-2"
        >
          {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : <FlaskConical className="h-4 w-4" />}
          Create Sandbox User
        </Button>
      </div>

      {credentials && (
        <HmrcSandboxCredentialsPanel
          credentials={credentials}
          enrolledServices={lastEnrolledServices}
          onDismiss={() => setCredentials(null)}
        />
      )}
    </div>
  );
}
